// The site chrome shared by the member and admin areas, after vocdoni-app src/elements/PublicLayout.tsx and
// src/components/Layout/Logo.tsx (the organization's logo in place of Vocdoni's).
import { Box, Flex, Link as CLink, Text, type BoxProps } from '@chakra-ui/react'
import { useConfigQuery } from '~/api/hooks'
import { useT } from '~/i18n/use-t'
import { NARROW, visuallyHidden } from '~/theme/media'

/** Same width and side padding for the header, the page and the footer, so their left edges line up. */
export const SiteContainer = (props: BoxProps) => (
  <Box
    w='full'
    maxW='1280px'
    mx='auto'
    // `--page-px` is the side padding, for children that bleed to the edges (the sticky action bar of a step).
    css={{
      '--page-px': 'var(--chakra-spacing-4)',
      '@media screen and (min-width: 48rem)': { '--page-px': 'var(--chakra-spacing-6)' },
      '@media screen and (min-width: 80rem)': { '--page-px': 'var(--chakra-spacing-10)' },
    }}
    px='var(--page-px)'
    {...props}
  />
)

/**
 * The organization's wordmark (vocdoni-app shows the organization's logo in its place): a monogram and the name. A
 * long name wraps onto a second line instead of shrinking to "O..." (mobile review, ruling M4); with `markOnlyWhenNarrow`
 * (the top bars) a screen under 360 px wide shows the monogram alone and keeps the name for screen readers.
 */
export function OrgMark({ markOnlyWhenNarrow = false }: { markOnlyWhenNarrow?: boolean }) {
  const name = useConfigQuery().data?.org.name
  if (!name) return <Box />
  return (
    <Flex align='center' gap={2.5} minW={0} title={name}>
      <Flex
        aria-hidden
        flexShrink={0}
        w={8}
        h={8}
        borderRadius='md'
        bg='fg'
        color='bg'
        align='center'
        justify='center'
        fontWeight='bolder'
        fontSize='md'
      >
        {name.trim().charAt(0).toUpperCase()}
      </Flex>
      <Text
        as='span'
        fontWeight='bold'
        fontSize='md'
        letterSpacing='-0.01em'
        lineHeight='1.25'
        lineClamp={2}
        overflowWrap='anywhere'
        minW='0'
        css={markOnlyWhenNarrow ? { [NARROW]: visuallyHidden } : undefined}
      >
        {name}
      </Text>
    </Flex>
  )
}

/** "Skip to the main content": hidden until it has the keyboard focus. */
export function SkipLink() {
  const { t } = useT()
  return (
    <CLink
      href='#main'
      display='inline-flex'
      alignItems='center'
      minH='12'
      position='absolute'
      left={4}
      top={2}
      zIndex='top'
      bg='bg'
      px={3}
      py={2}
      borderRadius='sm'
      borderWidth='1px'
      transform='translateY(-200%)'
      _focusVisible={{ transform: 'none' }}
    >
      {t('common.skipToContent')}
    </CLink>
  )
}
